import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, parseISO, addMonths } from 'date-fns';
import { RefreshCw, TrendingUp, Clock, ChevronLeft, ChevronRight, FileDown, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';
import AppHeader from '../components/AppHeader';
import M45NavPills from '../components/M45NavPills';

const enconOrange = '#ee7203';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PriceIncreases = ({ user }) => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastScraped, setLastScraped] = useState(null);
  
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfMonths, setPdfMonths] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(''); // Filter by supplier
  
  // Quick Links
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  
  // Month selector - default to current month
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-indexed

  useEffect(() => {
    loadPriceIncreases();
    loadQuickLinks();
  }, []);

  const loadPriceIncreases = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API}/price-increases`);
      setEntries(response.data.entries || []);
      setLastScraped(response.data.last_scraped);
    } catch (error) {
      console.error('Error loading price increases:', error);
      toast.error('Failed to load price increases');
    } finally {
      setLoading(false);
    }
  };

  const loadQuickLinks = async () => {
    try {
      const [linksRes, groupsRes] = await Promise.all([
        api.get(`${API}/quick-links`),
        api.get(`${API}/quick-link-groups`)
      ]);
      setQuickLinks(linksRes.data || []);
      setLinkGroups(groupsRes.data || []);
    } catch (error) {
      // Quick links are optional, don't block the page
    }
  };

  const getLinksForGroup = (groupId) => {
    return quickLinks.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);
  };

  const handleQuickLinkClick = (link) => {
    if (link.url) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`${API}/price-increases/refresh`);
      toast.success('Price increases refreshed');
      await loadPriceIncreases();
    } catch (error) {
      console.error('Error refreshing:', error);
      toast.error('Failed to refresh price increases');
    } finally {
      setRefreshing(false);
    }
  };

  // Filter entries for selected month and next month
  const getEntriesForMonth = (year, month) => {
    const monthStart = startOfMonth(new Date(year, month));
    const monthEnd = endOfMonth(new Date(year, month));
    
    return entries.filter(entry => {
      try {
        const dateStr = entry.effective_date_iso;
        if (!dateStr) return false;
        const entryDate = parseISO(dateStr);
        return entryDate >= monthStart && entryDate <= monthEnd;
      } catch {
        return false;
      }
    });
  };

  // Group entries by supplier
  const groupBySupplier = (monthEntries) => {
    const bySupplier = {};
    monthEntries.forEach(entry => {
      const key = entry.supplier_name;
      if (!bySupplier[key]) {
        bySupplier[key] = {
          supplier_name: entry.supplier_name,
          logo_url: entry.logo_url,
          increases: []
        };
      }
      bySupplier[key].increases.push(entry);
    });
    return Object.values(bySupplier).sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));
  };

  // Selected month data
  const selectedMonthEntries = useMemo(() => getEntriesForMonth(selectedYear, selectedMonth), [entries, selectedYear, selectedMonth]);
  const selectedMonthSuppliers = useMemo(() => groupBySupplier(selectedMonthEntries), [selectedMonthEntries]);

  // Next month data
  const nextMonth = selectedMonth === 11 ? 0 : selectedMonth + 1;
  const nextYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
  const nextMonthEntries = useMemo(() => getEntriesForMonth(nextYear, nextMonth), [entries, nextYear, nextMonth]);
  const nextMonthSuppliers = useMemo(() => groupBySupplier(nextMonthEntries), [nextMonthEntries]);

  const selectedMonthLabel = format(new Date(selectedYear, selectedMonth), 'MMMM yyyy');
  const nextMonthLabel = format(new Date(nextYear, nextMonth), 'MMMM yyyy');

  // Get all unique suppliers for the dropdown filter
  const uniqueSuppliers = useMemo(() => {
    const names = new Set();
    entries.forEach(e => { if (e.supplier_name) names.add(e.supplier_name); });
    return Array.from(names).sort();
  }, [entries]);

  // When supplier is selected: show all 12 months of the selected year
  // When "All Suppliers": show current month + next month only
  const handleSupplierChange = (value) => {
    setSelectedSupplier(value);
    if (value) {
      // Jump to current year to show all months for this supplier
      setSelectedYear(new Date().getFullYear());
      setSelectedMonth(0); // Start from January
    } else {
      // Back to current month
      setSelectedYear(new Date().getFullYear());
      setSelectedMonth(new Date().getMonth());
    }
  };

  // Build month data to render
  const monthsToRender = useMemo(() => {
    if (selectedSupplier) {
      // Show all 12 months of the selected year for this supplier
      const months = [];
      for (let m = 0; m < 12; m++) {
        const monthEntries = getEntriesForMonth(selectedYear, m);
        const allSuppliers = groupBySupplier(monthEntries);
        const filtered = allSuppliers.filter(s => s.supplier_name === selectedSupplier);
        if (filtered.length > 0) {
          months.push({
            label: format(new Date(selectedYear, m), 'MMMM yyyy'),
            suppliers: filtered,
            count: filtered.length
          });
        }
      }
      return months;
    } else {
      // Normal mode: current month + next month
      return [
        { label: selectedMonthLabel, suppliers: selectedMonthSuppliers, count: selectedMonthSuppliers.length },
        { label: nextMonthLabel, suppliers: nextMonthSuppliers, count: nextMonthSuppliers.length }
      ];
    }
  }, [selectedSupplier, selectedYear, selectedMonth, entries, selectedMonthLabel, nextMonthLabel, selectedMonthSuppliers, nextMonthSuppliers]);

  // Build available months for PDF selection (next 12 months that have data)
  const availablePdfMonths = useMemo(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const d = addMonths(new Date(), i);
      const y = d.getFullYear();
      const m = d.getMonth();
      const monthEntries = getEntriesForMonth(y, m);
      months.push({
        year: y,
        month: m,
        label: format(d, 'MMMM yyyy'),
        count: groupBySupplier(monthEntries).length,
        key: `${y}-${m}`
      });
    }
    return months;
  }, [entries]);

  const togglePdfMonth = (key) => {
    setPdfMonths(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const openPdfModal = () => {
    // Pre-select current month and next month
    const current = `${selectedYear}-${selectedMonth}`;
    const next = `${nextYear}-${nextMonth}`;
    setPdfMonths([current, next]);
    setShowPdfModal(true);
  };

  const generatePdf = async (mode = 'print') => {
    if (pdfMonths.length === 0) {
      toast.error('Please select at least one month');
      return;
    }
    setGeneratingPdf(true);
    try {
      const monthsData = pdfMonths
        .map(key => {
          const [y, m] = key.split('-').map(Number);
          const monthEntries = getEntriesForMonth(y, m);
          const suppliers = groupBySupplier(monthEntries);
          return { label: format(new Date(y, m), 'MMMM yyyy'), suppliers };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      const html = buildPdfHtml(monthsData);
      
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      
      if (mode === 'print') {
        printWindow.onload = () => { printWindow.print(); };
      } else {
        // For download: use the browser's print-to-PDF with save
        printWindow.onload = () => {
          // Set filename hint via title
          printWindow.document.title = `Price_Increases_${format(new Date(), 'dd-MM-yyyy')}`;
          printWindow.print();
        };
      }
      
      setShowPdfModal(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const buildPdfHtml = (monthsData) => {
    return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Price Increases Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #ee7203; padding-bottom: 15px; }
            .header h1 { color: #ee7203; font-size: 24px; }
            .header p { color: #888; font-size: 12px; margin-top: 5px; }
            .month-section { margin-bottom: 20px; }
            .month-title { background: #ee7203; color: white; padding: 8px 16px; font-size: 16px; font-weight: bold; border-radius: 6px; margin-bottom: 12px; display: inline-block; }
            .supplier-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
            .supplier-card { border: 1px solid #ddd; border-radius: 8px; padding: 10px; break-inside: avoid; }
            .supplier-name { font-weight: bold; font-size: 13px; color: #333; border-bottom: 1px solid #eee; padding-bottom: 6px; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
            .supplier-logo { height: 24px; width: auto; max-width: 60px; object-fit: contain; }
            .increase-row { display: flex; justify-content: space-between; font-size: 11px; color: #666; margin-bottom: 4px; }
            .increase-amount { font-weight: bold; color: #ee7203; }
            .product-list { list-style: none; padding: 0; }
            .product-list li { font-size: 10px; color: #666; padding: 1px 0; padding-left: 10px; position: relative; }
            .product-list li::before { content: ''; position: absolute; left: 0; top: 6px; width: 4px; height: 4px; background: #ee7203; border-radius: 50%; }
            .no-data { text-align: center; color: #999; padding: 20px; font-size: 13px; }
            @media print { 
              body { padding: 10px; }
              .supplier-grid { grid-template-columns: repeat(3, 1fr); } 
              .supplier-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Upcoming Price Increases</h1>
            <p>Generated ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
          </div>
          ${monthsData.map(month => `
            <div class="month-section">
              <div class="month-title">${month.label}</div>
              ${month.suppliers.length === 0 
                ? '<div class="no-data">No price increases for this month</div>'
                : `<div class="supplier-grid">
                    ${month.suppliers.map(supplier => `
                      <div class="supplier-card">
                        <div class="supplier-name">
                          ${supplier.logo_url ? `<img class="supplier-logo" src="${supplier.logo_url}" onerror="this.style.display='none'" />` : ''}
                          ${supplier.supplier_name}
                        </div>
                        ${supplier.increases.map(inc => `
                          <div class="increase-row">
                            <span>${inc.effective_date}</span>
                            <span class="increase-amount">${inc.increase_amount || 'TBC'}</span>
                          </div>
                          ${inc.products && inc.products.length > 0 ? `
                            <ul class="product-list">
                              ${inc.products.map(p => `<li>${p}</li>`).join('')}
                            </ul>
                          ` : ''}
                        `).join('')}
                      </div>
                    `).join('')}
                  </div>`
              }
            </div>
          `).join('')}
        </body>
        </html>
      `;
  };

  const renderSupplierGrid = (suppliers, label, count) => (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="px-5 py-2 rounded-xl" style={{ backgroundColor: enconOrange }}>
          <span className="text-white text-lg font-bold">{label}</span>
        </div>
        <span className="text-sm font-semibold text-gray-500">{count} supplier{count !== 1 ? 's' : ''}</span>
      </div>
      
      {suppliers.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm bg-white rounded-xl border">
          No price increases announced for {label}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {suppliers.map((supplier, sIdx) => (
            <div
              key={sIdx}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              data-testid={`supplier-card-${supplier.supplier_name}`}
            >
              {/* Supplier Logo + Name */}
              <div className="flex items-center gap-3 p-3 border-b bg-gray-50">
                {supplier.logo_url ? (
                  <img
                    src={supplier.logo_url}
                    alt={supplier.supplier_name}
                    className="h-10 w-auto max-w-[100px] object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-10 w-10 rounded bg-gray-200 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <span className="font-bold text-sm text-gray-800 truncate flex-1">
                  {supplier.supplier_name}
                </span>
              </div>

              {/* Increase Details */}
              <div className="p-3">
                {supplier.increases.map((inc, iIdx) => (
                  <div key={iIdx} className={iIdx > 0 ? 'mt-3 pt-3 border-t border-gray-100' : ''}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-gray-500">{inc.effective_date}</span>
                      <span className="text-sm font-bold" style={{ color: enconOrange }}>
                        {inc.increase_amount || 'TBC'}
                      </span>
                    </div>
                    {inc.products && inc.products.length > 0 && (
                      <ul className="space-y-0.5">
                        {inc.products.map((product, pIdx) => (
                          <li key={pIdx} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: enconOrange }} />
                            {product}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        user={user}
        subHeaderLeft={
          <>
            <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
              <TrendingUp className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
              <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Price Increases</span>
            </div>
            <select
              value={selectedSupplier}
              onChange={(e) => handleSupplierChange(e.target.value)}
              className="px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-white cursor-pointer"
              data-testid="supplier-filter-dropdown"
            >
              <option value="">All Suppliers</option>
              {uniqueSuppliers.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </>
        }
        subHeaderRight={
          <>
            {lastScraped && (
              <p className="text-[11px] text-white/80 mr-1 hidden md:block">
                <Clock className="w-3 h-3 inline mr-1" />
                {format(new Date(lastScraped), 'dd/MM HH:mm')}
              </p>
            )}
            <button
              onClick={openPdfModal}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
              style={{ color: enconOrange }}
              data-testid="pdf-prices-btn"
            >
              <FileDown className="w-3.5 h-3.5" />
              PDF
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
              style={{ color: enconOrange }}
              data-testid="refresh-prices-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </>
        }
      />
      
      <div className="pt-[20px] px-4 md:px-6 pb-6 bg-gray-100 min-h-screen">
        <div className="max-w-[1800px] mx-auto">

        {/* Month Selector - show when viewing all suppliers */}
        {!selectedSupplier && (
        <div className="bg-white rounded-xl border-2 shadow-sm mb-6 overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center">
            {/* Prev Year Arrow */}
            <button
              onClick={() => setSelectedYear(y => y - 1)}
              className="flex-shrink-0 p-3 hover:bg-gray-100 transition-colors"
              data-testid="prev-year-btn"
            >
              <ChevronLeft className="w-5 h-5" style={{ color: enconOrange }} />
            </button>

            {/* Year */}
            <button
              className="flex-shrink-0 px-4 py-3 font-bold text-lg bg-gray-100 hover:bg-gray-200 transition-colors min-w-[80px] text-center"
              style={{ color: '#333' }}
              data-testid="year-display"
            >
              {selectedYear}
            </button>

            {/* Month Buttons */}
            <div className="flex flex-1">
              {MONTHS.map((month, idx) => {
                const isActive = idx === selectedMonth;
                return (
                  <button
                    key={month}
                    onClick={() => setSelectedMonth(idx)}
                    className={`flex-1 py-3 text-sm font-semibold transition-all ${
                      isActive ? 'text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    style={isActive ? { backgroundColor: enconOrange } : {}}
                    data-testid={`month-btn-${idx}`}
                  >
                    {month}
                  </button>
                );
              })}
            </div>

            {/* Next Year Arrow */}
            <button
              onClick={() => setSelectedYear(y => y + 1)}
              className="flex-shrink-0 p-3 hover:bg-gray-100 transition-colors"
              data-testid="next-year-btn"
            >
              <ChevronRight className="w-5 h-5" style={{ color: enconOrange }} />
            </button>
          </div>
        </div>
        )}

        {/* Year selector when viewing a single supplier */}
        {selectedSupplier && (
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setSelectedYear(y => y - 1)}
            className="p-2 rounded-lg border-2 hover:bg-gray-100 transition-colors"
            style={{ borderColor: enconOrange }}
            data-testid="supplier-prev-year"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: enconOrange }} />
          </button>
          <span className="text-xl font-bold px-4 py-2 rounded-xl bg-white border-2" style={{ borderColor: enconOrange, color: enconOrange }}>
            {selectedYear}
          </span>
          <button
            onClick={() => setSelectedYear(y => y + 1)}
            className="p-2 rounded-lg border-2 hover:bg-gray-100 transition-colors"
            style={{ borderColor: enconOrange }}
            data-testid="supplier-next-year"
          >
            <ChevronRight className="w-5 h-5" style={{ color: enconOrange }} />
          </button>
          <span className="text-sm text-gray-500">Showing all {selectedSupplier} increases for {selectedYear}</span>
        </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500">Loading price increases...</p>
          </div>
        ) : (
          <>
            {monthsToRender.length === 0 && selectedSupplier ? (
              <div className="text-center py-12 text-gray-400">
                No price increases found for {selectedSupplier} in {selectedYear}
              </div>
            ) : (
              monthsToRender.map((month, idx) => (
                <div key={idx}>
                  {renderSupplierGrid(month.suppliers, month.label, month.count)}
                </div>
              ))
            )}
          </>
        )}

        {/* PDF Month Selection Modal */}
        <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: enconOrange }}>
                <FileDown className="w-5 h-5" />
                Generate PDF
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-gray-600">Select which months to include:</p>
              
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {availablePdfMonths.map(month => (
                  <div
                    key={month.key}
                    onClick={() => togglePdfMonth(month.key)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                      pdfMonths.includes(month.key) ? 'bg-orange-50 border-orange-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        pdfMonths.includes(month.key) ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                      }`}
                    >
                      {pdfMonths.includes(month.key) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="font-medium text-sm flex-1">{month.label}</span>
                    <span className="text-xs text-gray-400">
                      {month.count > 0 ? `${month.count} supplier${month.count !== 1 ? 's' : ''}` : 'No data'}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-end pt-3 border-t">
                <Button variant="outline" onClick={() => setShowPdfModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => generatePdf('print')}
                  disabled={generatingPdf || pdfMonths.length === 0}
                  className="bg-white border-2 rounded-xl"
                  style={{ borderColor: enconOrange, color: enconOrange }}
                  data-testid="print-pdf-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button
                  onClick={() => generatePdf('download')}
                  disabled={generatingPdf || pdfMonths.length === 0}
                  className="text-white rounded-xl"
                  style={{ backgroundColor: enconOrange }}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <p className="text-[10px] text-gray-400 self-center">Download: select "Save as PDF" as destination</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
};

export default PriceIncreases;
